// Purpose: top navigation shell used across authenticated + public
// pages. Matches the sign-in mockup's top nav: gradient logo box on the
// left, wordmark, and a right-side slot for per-page controls (theme
// toggle, status pill, user menu, etc.).
//
// Variants:
//   * default — logo + wordmark + nav links + right slot
//   * logoOnly — logo + wordmark + right slot (no nav links). Used on
//     sign-in and other chromeless pages.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PadlockIcon } from '../icons/index.tsx';

export interface AppHeaderProps {
  /** When true, omits the primary nav links. */
  logoOnly?: boolean;
  /** Slot placed at the far right of the nav. */
  rightSlot?: ReactNode;
  /** Primary nav children; rendered centered-right when present. */
  children?: ReactNode;
  /** Extra className on the outer <nav>. */
  className?: string;
}

export function AppHeader({ logoOnly = false, rightSlot, children, className }: AppHeaderProps) {
  return (
    <nav
      aria-label="Site"
      className={[
        'absolute top-8 left-12 right-12 z-20',
        'flex items-center justify-between',
        className ?? '',
      ].join(' ')}
    >
      <Link to="/" className="flex items-center gap-3 text-inherit no-underline">
        {/* Gradient logo box. Brand colors are static across themes; the
            gradient uses the dark-mode stops (blue→indigo→violet) which
            read well against both backgrounds. */}
        <span
          aria-hidden
          className={[
            'flex items-center justify-center',
            'h-9 w-9 rounded-md text-white',
            'bg-[linear-gradient(135deg,#4F8CFF_0%,#6366F1_50%,#8B5CF6_100%)]',
            'shadow-logo-box',
          ].join(' ')}
        >
          <PadlockIcon size={20} strokeWidth={2.2} />
        </span>
        <span className="flex flex-col gap-[2px] leading-none">
          <span className="text-[13px] font-semibold tracking-[0.02em] text-fg-primary">
            Apogee
          </span>
          <span className="text-eyebrow uppercase text-fg-muted">Secure Transfer</span>
        </span>
      </Link>

      {/* Center/right: optional nav links when !logoOnly. */}
      {!logoOnly && children ? <div className="flex items-center gap-6">{children}</div> : null}

      {/* Right slot: theme toggle + status pill + user menu, etc. */}
      {rightSlot ? <div className="flex items-center gap-6">{rightSlot}</div> : null}
    </nav>
  );
}
